import { startOfDay, endOfDay, startOfWeek, startOfMonth, subDays } from 'date-fns';
import { z } from 'zod';
import type { DateRange, DateRangePreset } from '@/types/daterange';

const DATE_RANGE_KEY = 'kanjoy:dateRange';

const RELATIVE_PRESETS: ReadonlySet<DateRangePreset> = new Set([
  'today',
  'thisWeek',
  'thisMonth',
  'last7',
  'last30',
]);

function toISO(d: Date): string {
  // Format local Y-M-D — not UTC. `d.toISOString().slice(0,10)` is
  // off-by-one in any timezone east of UTC for Date objects at local
  // midnight or end-of-day, because toISOString shifts into UTC first.
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function isRelativePreset(p: DateRangePreset | undefined): p is DateRangePreset {
  return p !== undefined && RELATIVE_PRESETS.has(p);
}

export function resolvePreset(preset: DateRangePreset, now: Date): { start: string; end: string } {
  switch (preset) {
    case 'today':
      return { start: toISO(startOfDay(now)), end: toISO(endOfDay(now)) };
    case 'thisWeek':
      return { start: toISO(startOfWeek(now)), end: toISO(endOfDay(now)) };
    case 'thisMonth':
      return { start: toISO(startOfMonth(now)), end: toISO(endOfDay(now)) };
    case 'last7':
      return {
        start: toISO(startOfDay(subDays(now, 6))),
        end: toISO(endOfDay(now)),
      };
    case 'last30':
      return {
        start: toISO(startOfDay(subDays(now, 29))),
        end: toISO(endOfDay(now)),
      };
    case 'custom':
      throw new Error(
        'resolvePreset cannot resolve "custom" — caller must pass literal start/end.',
      );
  }
}

export function getDefaultDateRange(now: Date): DateRange {
  const { start, end } = resolvePreset('last7', now);
  return { start, end, preset: 'last7' };
}

const presetSchema = z.enum(['today', 'thisWeek', 'thisMonth', 'last7', 'last30', 'custom']);
const iso = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const storedRangeSchema = z.object({
  start: iso,
  end: iso,
  preset: presetSchema.optional(),
});

export function readStoredDateRange(now: Date): DateRange {
  let raw: string | null;
  try {
    raw = localStorage.getItem(DATE_RANGE_KEY);
  } catch {
    return getDefaultDateRange(now);
  }
  if (raw === null) return getDefaultDateRange(now);

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return getDefaultDateRange(now);
  }

  const result = storedRangeSchema.safeParse(parsed);
  if (!result.success) return getDefaultDateRange(now);

  const stored = result.data;
  if (isRelativePreset(stored.preset)) {
    const { start, end } = resolvePreset(stored.preset, now);
    return { start, end, preset: stored.preset };
  }
  return stored;
}

export function writeStoredDateRange(range: DateRange): void {
  try {
    localStorage.setItem(DATE_RANGE_KEY, JSON.stringify(range));
  } catch {
    // localStorage unavailable (privacy mode) — silently skip; next mount
    // will fall through to the default, matching the mockupMode pattern.
  }
}

export const DATE_RANGE_STORAGE_KEY = DATE_RANGE_KEY;
